/**
 * Supabase client — singleton used everywhere in the web app.
 *
 * Reads VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY from the environment.
 * In dev these come from apps/web/.env.local.
 * In production they come from Cloudflare Pages env vars.
 *
 * Auth flow: Authorization Code with PKCE (OAuth 2.0 / RFC 7636) for every
 * entry point — Google and Microsoft sign-in, magic links, signup
 * confirmation and password recovery. Tokens never travel in a URL fragment:
 * Supabase returns a one-time `?code=`, and the SDK exchanges it for a session
 * using the code verifier it generated when the flow started.
 *
 * Consequence: a magic link or recovery link only completes in the browser
 * that requested it (the verifier lives in that browser's storage). A link
 * opened on another device gets a clear "open it where you requested it"
 * message from routes/AuthCallback.tsx and a one-click path to a fresh link.
 * That trade is deliberate — the implicit flow that made cross-device links
 * work is deprecated (OAuth 2.0 Security BCP) and is disallowed by CASA / ASVS.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  console.error(
    '[supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. ' +
      'Add them to apps/web/.env.local (dev) or Cloudflare Pages env vars (production).'
  );
}

export const supabase: SupabaseClient = createClient(url ?? '', anonKey ?? '', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  },
});
